// react
import { SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// types
import {
	ActionButton,
	Document,
	Entity,
	Loan,
	Pagination,
	Transaction,
	User,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import {
	Link as RouterLink,
	Outlet,
	useLocation,
	useParams,
	useSearchParams,
	useNavigate,
} from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// context
import { ContextProject, ContextUserProjectPermissions } from '../Project/Project.context';
import {
	ContextBeneficiary,
	ContextBeneficiaryDocuments,
	ContextBeneficiaryCashBenefits,
	ContextBeneficiaryUsers,
	ContextBeneficiaryNonCashBenefits,
} from './Beneficiary.context';

// mui
import { Grid } from '@mui/material';

// pages
import { Details } from './Details/Details';

// dialogs
import { DialogAddNonCashBenefit } from '../../dialogs/DialogAddNonCashBenefit/DialogAddNonCashBenefit';
import { DialogAddTransaction } from '../../dialogs/DialogAddTransaction/DialogAddTransaction';
import { DialogAddUser } from '../../dialogs/DialogAddUser/DialogAddUser';
import { DialogUploadDocs } from '../../dialogs/DialogUploadDocs/DialogUploadDocs';

// ui
import { Breadcrumbs } from '../../ui/Breadcrumbs/Breadcrumbs';
import { Empty } from '../../ui/Empty/Empty';
import { entityIconSubtypes } from '../../ui/IconCircle/IconCircle';
import { Loading } from '../../ui/Loading/Loading';
import { Tabs } from '../../ui/Tabs/Tabs';
import { Title } from '../../ui/Title/Title';

// defaults
import { defaultPagination } from '../../../utils/defaults';

export const PageBeneficiary = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { id, idBeneficiary } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramBeneficiary = searchParams.get('beneficiary');
	const paramDialog = searchParams.get('dialog');
	const paramExpenseType = searchParams.get('expenseType');
	const paramLoan = searchParams.get('loan');
	const paramPage = Number(searchParams.get('page') || 1);
	const paramQ = searchParams.get('q');
	const paramStatus = searchParams.get('status');

	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// defaults
	const tabs = useMemo(() => {
		const tabs = [];

		if (userProjectPermissions?.cashBenefit?.read) {
			tabs.push({
				component: RouterLink,
				label: t('cashBenefits'),
				to: `/project/${id}/beneficiary/${idBeneficiary}/cash-benefits`,
				value: 'cashBenefits',
			});
		}

		if (userProjectPermissions?.nonCashBenefit?.read) {
			tabs.push({
				component: RouterLink,
				label: t('nonCashBenefits'),
				to: `/project/${id}/beneficiary/${idBeneficiary}/non-cash-benefits`,
				value: 'nonCashBenefits',
			});
		}

		if (userProjectPermissions?.user?.read) {
			tabs.push({
				component: RouterLink,
				label: t('users'),
				to: `/project/${id}/beneficiary/${idBeneficiary}/users`,
				value: 'users',
			});
		}

		if (userProjectPermissions?.document?.read) {
			tabs.push({
				component: RouterLink,
				label: t('documents'),
				to: `/project/${id}/beneficiary/${idBeneficiary}/documents`,
				value: 'documents',
			});
		}

		return tabs;
	}, [id, idBeneficiary, t, userProjectPermissions]);

	// defaults
	const getTabByLocation = useCallback(
		(pathname) => {
			if (pathname.includes('non-cash-benefits')) {
				return 'nonCashBenefits';
			} else if (pathname.includes('cash-benefits')) {
				return 'cashBenefits';
			} else if (pathname.includes('documents')) {
				return 'documents';
			} else if (pathname.includes('users')) {
				return 'users';
			} else {
				return tabs?.[0]?.value;
			}
		},
		[tabs]
	);

	const defaultTabValue = useMemo(() => {
		return getTabByLocation(location.pathname);
	}, [getTabByLocation, location]);

	// state
	const [beneficiary, setBeneficiary] = useState<Entity | null>(null);
	const [cashBenefits, setCashBenefits] = useState<Transaction[]>([]);
	const [documents, setDocuments] = useState<Document[]>([]);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [nonCashBenefits, setNonCashBenefits] = useState<Transaction[]>([]);
	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});

	const [paginationCashBenefits, setPaginationCashBenefits] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [paginationNonCashBenefits, setPaginationNonCashBenefits] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [paginationUsers, setPaginationUsers] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [tabValue, setTabValue] = useState(defaultTabValue);
	const [users, setUsers] = useState<User[]>([]);

	// fetch beneficiary
	const { isLoading: isLoadingBeneficiary, refetch: refetchBeneficiary } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/entities/${idBeneficiary}?project=${project?.id}&fields=id,idExternal,loans,metadata,name,subtype,type,wallet&populate=wallet`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setBeneficiary(res.data[0]);
			}
		},
	});

	// fetch beneficiary traceability
	const { fetchRequest: fetchTraceability, isLoading: isLoadingTraceability } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/entities/${idBeneficiary}?project=${project?.id}&fields=traceability`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data?.[0]?.traceability) {
				setBeneficiary((beneficiaryOld) =>
					beneficiaryOld ? { ...beneficiaryOld, traceability: res.data[0].traceability } : null
				);
			}
		},
	});

	// fetch beneficiary loans
	const { isLoading: isLoadingLoans } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/loans?entity=${idBeneficiary}&fields=id,code,entities&populate=entities`,

		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res) {
				setLoans(res.data);
			}
		},
	});

	// fetch cash benefits
	let fetchCashBenefitsUrl = `${process.env.REACT_APP_API_URL}/transactions?project=${project?.id}&page=${paramPage}&limit=${paginationCashBenefits.perPage}&type=CASH_BENEFIT&toEntity=${idBeneficiary}&populate=category,component,comments.user,expenseType,fromAccount,fromEntity,payments.conversions,payments.loan,toEntity`;

	if (paramLoan) {
		fetchCashBenefitsUrl += `&loan=${paramLoan}`;
	}
	if (paramStatus) {
		fetchCashBenefitsUrl += `&status=${paramStatus}`;
	}
	if (paramQ) {
		fetchCashBenefitsUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchCashBenefits,
		isLoading: isLoadingCashBenefits,
		refetch: refetchCashBenefits,
	} = useFetch({
		url: fetchCashBenefitsUrl,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCashBenefits(res.data);
			}

			if (res.pagination) {
				setPaginationCashBenefits(res.pagination);
			}
		},
	});

	// fetch beneficiary non cash benefits
	let fetchNonCashBenefitsUrl = `${process.env.REACT_APP_API_URL}/transactions?toEntity=${idBeneficiary}&type=NON_CASH_BENEFIT&page=${paramPage}&limit=${paginationNonCashBenefits.perPage}&populate=benefit,fromAccount,fromEntity`;

	if (paramStatus) {
		fetchNonCashBenefitsUrl += `&status=${paramStatus}`;
	}
	if (paramQ) {
		fetchNonCashBenefitsUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchNonCashBenefits,
		isLoading: isLoadingNonCashBenefits,
		refetch: refetchNonCashBenefits,
	} = useFetch({
		url: fetchNonCashBenefitsUrl,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setNonCashBenefits(res.data);
			}

			if (res.pagination) {
				setPaginationNonCashBenefits(res.pagination);
			}
		},
	});

	// fetch beneficiary users
	const userFields =
		'id,firstName,lastName,title,email,phone,country,projects,entities,modified,created';
	let urlUsers = `${process.env.REACT_APP_API_URL}/users?entities=${idBeneficiary}&fields=${userFields}&populate=country,entities&page=${paramPage}&limit=${paginationUsers.perPage}`;

	if (paramQ) {
		urlUsers += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchUsers,
		isLoading: isLoadingUsers,
		refetch: refetchUsers,
	} = useFetch({
		isLazy: true,
		url: urlUsers,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setUsers(res.data);
			}

			if (res.pagination) {
				setPaginationUsers(res.pagination);
			}
		},
	});

	// fetch beneficiary documents
	const {
		fetchRequest: fetchDocuments,
		isLoading: isLoadingDocuments,
		refetch: refetchDocuments,
	} = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/documents?project=${project?.id}&entities=${beneficiary?.id}&page=${paramPage}&limit=${paginationDocuments.perPage}&fields=created,entities,notarizationHash,id,name,transaction,type,user&populate=entities,transaction,type,user`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocuments(res.data);
			}

			if (res.pagination) {
				setPaginationDocuments(res.pagination);
			}
		},
	});

	// vars
	const titleActions: ActionButton[] = [];

	if (tabValue === 'users' && userProjectPermissions?.user?.create) {
		titleActions.push({
			children: t('addUser'),
			onClick: () => {
				searchParams.set('dialog', 'addUser');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	if (tabValue === 'cashBenefits' && userProjectPermissions?.cashBenefit?.create) {
		titleActions.push({
			children: t('recordCashBenefit'),
			onClick: () => {
				searchParams.set('dialog', 'recordCashBenefit');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	if (tabValue === 'nonCashBenefits' && userProjectPermissions?.nonCashBenefit?.create) {
		titleActions.push({
			children: t('recordNonCashBenefit'),
			onClick: () => {
				searchParams.set('dialog', 'recordNonCashBenefit');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	if (tabValue === 'documents' && userProjectPermissions?.document?.create) {
		titleActions.push({
			children: t('uploadDocuments'),
			onClick: () => {
				searchParams.set('dialog', 'uploadDocsBeneficiary');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	let template;

	if (isLoadingBeneficiary) {
		template = <Loading variant="page" />;
	} else {
		if (beneficiary) {
			template = (
				<>
					<Grid container spacing={2}>
						<Grid item xs={11} lg={12}>
							<Breadcrumbs
								crumbs={[
									{
										children: t('projects'),
										to: '/projects',
									},
									{
										children: project?.idWbg,
										to: `/project/${id}`,
									},
									{
										children: t('beneficiaries'),
										to: `/project/${id}/beneficiaries`,
									},
								]}
								page={beneficiary.name}
							/>
						</Grid>
						<Grid item xs={12}>
							<Title
								actions={titleActions}
								ChipStatusProps={{
									status: beneficiary.wallet?.status || 'ACTIVE',
								}}
								color="magenta"
								IconCircleProps={{
									type: entityIconSubtypes[beneficiary.subtype],
								}}
								id={beneficiary.idExternal}
								overline={t('beneficiary')}
								title={beneficiary.name}
							/>
						</Grid>
						<Grid item xs={12}>
							<Details
								isLoadingLoans={isLoadingLoans}
								isLoadingTraceability={isLoadingTraceability}
								loans={loans}
								beneficiary={beneficiary}
							/>
						</Grid>
						<Grid item xs={12}>
							<Tabs onChange={handleChangeTab} tabs={tabs} value={tabValue} hasBorder={false} />
						</Grid>
						<Grid item xs={12}>
							<Outlet />
						</Grid>
					</Grid>
					<DialogAddUser
						entity={beneficiary}
						isOpen={paramDialog === 'addUser'}
						onClose={() => {
							searchParams.delete('dialog');
							setSearchParams(searchParams);
						}}
						onSuccess={() => {
							refetchUsers();
						}}
					/>
					<DialogAddTransaction
						isOpen={paramDialog === 'recordCashBenefit'}
						type="CASH_BENEFIT"
						onClose={() => {
							setSearchParams((params) => {
								params.delete('dialog');
								return params;
							});
						}}
						onSuccess={() => {
							refetchCashBenefits();
						}}
					/>

					<DialogAddNonCashBenefit
						isOpen={paramDialog === 'recordNonCashBenefit'}
						onClose={() => {
							setSearchParams((params) => {
								params.delete('dialog');
								return params;
							});
						}}
						onSuccess={() => {
							refetchNonCashBenefits();
						}}
					/>

					<DialogUploadDocs
						entity={beneficiary}
						isOpen={paramDialog === 'uploadDocsBeneficiary'}
						onClose={() => {
							searchParams.delete('dialog');
							setSearchParams(searchParams);
						}}
						onSuccess={() => {
							refetchDocuments();
						}}
					/>
				</>
			);
		} else {
			template = (
				<Empty
					actions={[
						{
							children: t('beneficiaries'),
							component: RouterLink,
							to: `/project/${project?.id}/beneficiaries`,
							variant: 'contained',
						},
					]}
					content={{ children: t('empty.beneficiary.content') }}
					title={{ children: t('empty.beneficiary.title') }}
					IconCircleProps={{ type: 'beneficiary' }}
					variant="page"
				/>
			);
		}
	}

	// fetch traceability when beneficiary loads
	useEffect(() => {
		if (beneficiary?.id) {
			fetchTraceability();
		}
	}, [beneficiary?.id, fetchTraceability]);

	// fetch data when tab changes
	useEffect(() => {
		if (tabValue === 'cashBenefits') {
			fetchCashBenefits();
		} else if (tabValue === 'nonCashBenefits') {
			fetchNonCashBenefits();
		} else if (tabValue === 'users') {
			fetchUsers();
		} else if (tabValue === 'documents') {
			if (project?.id && beneficiary?.id) {
				fetchDocuments();
			}
		}
	}, [
		fetchDocuments,
		fetchCashBenefits,
		fetchNonCashBenefits,
		fetchUsers,
		paramExpenseType,
		paramLoan,
		paramPage,
		paramQ,
		paramStatus,
		paramBeneficiary,
		project,
		beneficiary,
		tabValue,
	]);

	// set active tab when location changes
	useEffect(() => {
		const tabValueByLocation = getTabByLocation(location.pathname);
		setTabValue(tabValueByLocation);
	}, [getTabByLocation, location]);

	// navigate to first tab if path is not set
	useEffect(() => {
		const segments = location.pathname.split('/');
		if (segments.length < 6) {
			navigate(tabs[0].to);
		}
	}, [location, navigate, tabs]);

	return (
		<ContextBeneficiary.Provider
			value={{ beneficiary, isLoadingBeneficiary, refetchBeneficiary, setBeneficiary }}>
			<ContextBeneficiaryCashBenefits.Provider
				value={{
					isLoadingCashBenefits,
					paginationCashBenefits,
					cashBenefits,
					refetchCashBenefits,
					setCashBenefits,
					setPaginationCashBenefits,
				}}>
				<ContextBeneficiaryNonCashBenefits.Provider
					value={{
						isLoadingNonCashBenefits,
						paginationNonCashBenefits,
						nonCashBenefits,
						refetchNonCashBenefits,
						setNonCashBenefits,
						setPaginationNonCashBenefits,
					}}>
					<ContextBeneficiaryUsers.Provider
						value={{
							isLoadingUsers,
							paginationUsers,
							refetchUsers,
							setUsers,
							setPaginationUsers,
							users,
						}}>
						<ContextBeneficiaryDocuments.Provider
							value={{
								isLoadingDocuments,
								paginationDocuments,
								refetchDocuments,
								setDocuments,
								setPaginationDocuments,
								documents,
							}}>
							{template}
						</ContextBeneficiaryDocuments.Provider>
					</ContextBeneficiaryUsers.Provider>
				</ContextBeneficiaryNonCashBenefits.Provider>
			</ContextBeneficiaryCashBenefits.Provider>
		</ContextBeneficiary.Provider>
	);
};
